// Zero-Click Trial — public page at /trial/start
// Email-only form → magic link email → /trial/welcome?token=...
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Mail, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function TrialStart() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.includes("@")) { setErr("Please enter a valid email."); return; }
    setBusy(true); setErr("");
    try {
      const aff = typeof window !== "undefined" ? (sessionStorage.getItem("cr_aff") || new URLSearchParams(window.location.search).get("aff")) : null;
      const r = await fetch(`${API}/api/trial/magic-link/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, affiliate_slug: aff || null }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d?.detail || `Failed (HTTP ${r.status})`); return; }
      setSent(d);
    } catch (ex) { setErr(ex?.message || "Network error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-emerald-50 text-slate-900" data-testid="trial-start-page">
      <PublicHeader light={true}/>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-violet-700">Choose a plan</div>
        <h1 className="font-display text-3xl sm:text-5xl font-bold text-slate-900 mt-2 leading-tight">Choose a plan and get started in 60 seconds.</h1>
        <p className="text-base text-slate-600 mt-4 max-w-xl leading-relaxed">
          Enter your email to activate a secure Paid plan. Cancel any time. We&apos;ll send a one-time activation link so you can finish setup on any device.
        </p>

        {!sent ? (
          <Card className="p-5 mt-8">
            <form onSubmit={submit} className="space-y-3">
              <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 block">Your email</label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400"/>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="trial-email" placeholder="you@example.ca" autoFocus
                    className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500"/>
                </div>
                <button type="submit" disabled={busy} data-testid="trial-submit"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-semibold disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <ArrowRight className="w-4 h-4"/>}
                  {busy ? "Sending…" : "Choose a plan"}
                </button>
              </div>
              {err && <div className="text-xs text-rose-600" data-testid="trial-err">{err}</div>}
              <div className="text-[11px] text-slate-500">We&apos;ll never spam. Unsubscribe in 1 tap.</div>
            </form>
          </Card>
        ) : (
          <Card className="p-6 mt-8 border-emerald-200 bg-emerald-50/40" data-testid="trial-sent">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 mt-1 flex-none"/>
              <div>
                <div className="font-bold text-slate-900">Activation email sent to {sent.sent_to}</div>
                <p className="text-sm text-slate-600 mt-1">Open your inbox and tap <b>Activate my trial</b> to complete setup. The link stays valid for {sent.expires_in_min} minutes.</p>
                <p className="text-xs text-slate-500 mt-3">Didn&apos;t arrive? Check spam, or <button onClick={() => setSent(null)} className="underline">try a different email</button>.</p>
              </div>
            </div>
          </Card>
        )}

        <div className="mt-8 text-xs text-slate-500 flex flex-wrap items-center gap-4">
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600"/>Paid plans from CA$35/month</span>
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600"/>Cancel any time</span>
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600"/>Cancel anytime</span>
        </div>
      </div>
    </div>
  );
}
